import { createReducer, on } from "@ngrx/store";
import { IMutedSensor, initialCameraState, initialSnapshotState, getSnapshotKey } from "../camera.state";
import { CameraActions } from '../camera.actions'
import {
    onLoadableLoad,
    onLoadableSuccess,
    onLoadableError
} from '../../../models/index';
import {
    toLoadableWrapper,
} from "../../../models/loadable";
import { ADD_CAMERA_SNAPSHOT_ID } from "../../../constants/cameras";

export const cameraReducer = createReducer(
    initialCameraState,
    on(CameraActions.loadCameras, state => ({ ...onLoadableLoad(state) })),
    on(CameraActions.loadCamerasSuccess, (state, { sensors }) => ({ ...onLoadableSuccess(state), sensors: [...sensors] })),
    on(CameraActions.loadCamerasFailure, (state, { error }) => ({ ...onLoadableError(state, error) })),
    on(CameraActions.setSelectedSensor, (state, { sensor }) => ({ ...state, selectedSensor: sensor })),
    on(CameraActions.clearSelectedSensor, state => ({ ...state, selectedSensor: null })),
    on(CameraActions.loadSnapshot, (state, { sensor, showHeatmap }) => {
        const key = getSnapshotKey(sensor.id, showHeatmap);
        return {
            ...state,
            snapshots: {
                ...state.snapshots,
                [key]: {
                    ...onLoadableLoad({ ...(state.snapshots[key] ?? { ...initialSnapshotState }) })
                }
            }
        };
    }),
    on(CameraActions.loadSnapshotSuccess, (state, { sensor, url, retrievalTime, live, showHeatmap }) => {
        const key = getSnapshotKey(sensor.id, showHeatmap);
        return {
            ...state,
            snapshots: {
                ...state.snapshots,
                [key]: {
                    ...onLoadableSuccess({ ...(state.snapshots[key] ?? { ...initialSnapshotState }) }),
                    url,
                    last_retrieve: retrievalTime,
                    live
                }
            }
        };
    }),
    on(CameraActions.loadSnapshotFailure, (state, { sensor, error, live, showHeatmap }) => {
        const key = getSnapshotKey(sensor.id, showHeatmap);
        return {
            ...state,
            snapshots: {
                ...state.snapshots,
                [key]: {
                    ...onLoadableError({ ...(state.snapshots[key] ?? { ...initialSnapshotState }) }, error),
                    live
                }
            }
        };
    }),
    // overrideSnapshot only marks the entry as loading; the effect writes the image
    // to the cache and dispatches loadSnapshotSuccess with the resulting URL.
    on(CameraActions.overrideSnapshot, (state, { sensor, showHeatmap }) => {
        const key = getSnapshotKey(sensor.id, showHeatmap);
        return {
            ...state,
            snapshots: {
              ...state.snapshots,
              [key]: {
                  ...onLoadableLoad({ ...(state.snapshots[key] ?? { ...initialSnapshotState }) })
              }
          }
        };
    }),
    // No case for primeSnapshotFromLivestream: it would flash a spinner every 30s.
    on(CameraActions.setSnapshot, (state, { sensorId, url, retrievalTime }) => {
        const key = getSnapshotKey(sensorId, false);
        return {
            ...state,
            snapshots: {
                ...state.snapshots,
                [key]: {
                    ...onLoadableSuccess({ ...(state.snapshots[key] ?? { ...initialSnapshotState }) }),
                    url,
                    last_retrieve: retrievalTime,
                    live: false
                }
            }
        };
    }),
    // One state update for a whole burst of cached reads: a dispatch per camera would
    // re-run every card's selector on a site with 100+ cameras.
    on(CameraActions.setCachedSnapshots, (state, { snapshots }) => {
        const updated = { ...state.snapshots };
        for (const { sensorId, url, retrievalTime } of snapshots) {
            const key = getSnapshotKey(sensorId, false);
            updated[key] = {
                ...onLoadableSuccess({ ...(updated[key] ?? { ...initialSnapshotState }) }),
                url,
                last_retrieve: retrievalTime,
                live: false
            };
        }
        return { ...state, snapshots: updated };
    }),
    on(CameraActions.resetAddCameraSnapshot, (state) => {
        const remainingSnapshots = { ...state.snapshots };
        delete remainingSnapshots[ADD_CAMERA_SNAPSHOT_ID];
        return {
            ...state,
            snapshots: remainingSnapshots
        };
    }),
    on(CameraActions.resetSnapshot, (state, { snapshotID }) => {
        return {
            ...state,
            snapshots: {
                ...state.snapshots,
                [snapshotID]: {
                    ...initialSnapshotState
                }
            }
        }
    }),
    on(CameraActions.loadMutedSensors, (state) => ({
        ...state,
        mutedSensors: { ...onLoadableLoad(state.mutedSensors) }
    })),
    on(CameraActions.loadMutedSensorsSuccess, (state, { mutedSensors }) => ({
        ...state,
        mutedSensors: {
          ...onLoadableSuccess(state.mutedSensors),
          ...mutedSensors.reduce((acc: Record<string, IMutedSensor>, curr: IMutedSensor) => {
              if (curr.id != null) {
                  acc[curr.id] = curr;
              }
              return acc;
          }, {})
        }
    })),
    on(CameraActions.loadMutedSensorsFailure, (state, { error }) => ({
        ...state,
        mutedSensors: { ...onLoadableError(state.mutedSensors, error) }
    })),
    on(CameraActions.muteSensors, (state) => ({
        ...state,
        mutedSensors: { ...onLoadableLoad(state.mutedSensors) }
    })),
    on(CameraActions.muteSensorsSuccess, (state, { ids, muteReason, muteTimestamp, muteExpiryTimestamp }) => ({
        ...state,
        mutedSensors: {
          ...onLoadableSuccess(state.mutedSensors),
          ...ids.reduce((acc: Record<string, IMutedSensor>, curr: string) => {
              acc[curr] = {
                  mute_reason: muteReason,
                  mute_timestamp: muteTimestamp,
                  mute_expiry_timestamp: muteExpiryTimestamp
              }
              return acc;
          }, {})
      }
    })),
    on(CameraActions.muteSensorsFailure, (state, { error }) => ({
        ...state,
        mutedSensors: { ...onLoadableError(state.mutedSensors, error) }
    })),
    on(CameraActions.unmuteSensors, (state) => ({
        ...state,
        mutedSensors: { ...onLoadableLoad(state.mutedSensors) }
    })),
    on(CameraActions.unmuteSensorsSuccess, (state, { ids }) => ({
        ...state,
        mutedSensors: {
            ...onLoadableSuccess(
              toLoadableWrapper(Object.fromEntries(Object.entries(state.mutedSensors).filter(([key]) => !ids.includes(key))))
            )
        }
    })),
    on(CameraActions.unmuteSensorsFailure, (state, { error }) => ({
        ...state,
        mutedSensors: { ...onLoadableError(state.mutedSensors, error) }
    })),
    on(CameraActions.muteBySimilarity, (state) => ({
        ...state,
        similarityMuteState: { ...onLoadableLoad(state.similarityMuteState) }
    })),
    on(CameraActions.muteBySimilaritySuccess, (state) => ({
        ...state,
        similarityMuteState: { ...onLoadableSuccess(state.similarityMuteState) }
    })),
    on(CameraActions.muteBySimilarityFailure, (state, { error }) => ({
        ...state,
        similarityMuteState: { ...onLoadableError(state.similarityMuteState, error) }
    })),
    on(CameraActions.clearExpiredMutedSensors, (state) => ({
        ...state,
        mutedSensors: {
            ...onLoadableSuccess(
              toLoadableWrapper(Object.fromEntries(Object.entries(state.mutedSensors).filter(([, value]) => new Date(value?.mute_expiry_timestamp) > new Date())))
            )
        }
    })),
);
